import React from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

/**
 * MessageBubble - A reusable chat message bubble component
 * @param {Object} props
 * @param {Object} props.message - Message object
 * @param {boolean} props.isAdmin - Is admin message
 */
const MessageBubble = ({ message, isAdmin }) => {
  return (
    <ArgonBox
      sx={{
        display: "flex",
        justifyContent: isAdmin ? "flex-end" : "flex-start",
        mb: 2,
        animation: "slideIn 0.3s ease",
        "@keyframes slideIn": {
          from: {
            opacity: 0,
            transform: isAdmin ? "translateX(20px)" : "translateX(-20px)",
          },
          to: {
            opacity: 1,
            transform: "translateX(0)",
          },
        },
      }}
    >
      <ArgonBox
        sx={{
          maxWidth: "75%",
          p: 2.5,
          borderRadius: isAdmin ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
          background: isAdmin ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "#ffffff",
          color: isAdmin ? "#fff" : "#334155",
          boxShadow: isAdmin
            ? "0 8px 24px rgba(102, 126, 234, 0.3)"
            : "0 4px 16px rgba(0, 0, 0, 0.1)",
          border: isAdmin ? "none" : "1px solid #e2e8f0",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: isAdmin
              ? "0 12px 32px rgba(102, 126, 234, 0.4)"
              : "0 8px 24px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
          <ArgonBox
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: isAdmin ? "rgba(255, 255, 255, 0.5)" : "#667eea",
            }}
          />
          <ArgonTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              color: isAdmin ? "rgba(255, 255, 255, 0.9)" : "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {message.senderName}
          </ArgonTypography>
        </ArgonBox>

        <ArgonTypography
          variant="body2"
          sx={{
            color: isAdmin ? "#fff" : "#334155",
            lineHeight: 1.6,
            fontSize: "0.938rem",
            wordBreak: "break-word",
          }}
        >
          {message.text}
        </ArgonTypography>

        <ArgonTypography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            opacity: 0.7,
            fontSize: "0.75rem",
            color: isAdmin ? "#fff" : "#94a3b8",
          }}
        >
          {message.timestamp}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    senderName: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
  }).isRequired,
  isAdmin: PropTypes.bool,
};

MessageBubble.defaultProps = {
  isAdmin: false,
};

export default MessageBubble;
